import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import LanguageIcon from '@mui/icons-material/Language';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import { format } from 'date-fns';

// Available language codes to choose from
const AVAILABLE_LANGUAGES = [
  { code: 'ja-JP', name: 'Japanese (Japan)' },
  { code: 'en-US', name: 'English (US)' },
  { code: 'id-ID', name: 'Indonesian' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
];

const TagCard = ({ tag, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTag, setEditedTag] = useState(null);
  const [newLanguage, setNewLanguage] = useState('');

  // Helper function to get chip styling based on rule type
  const getChipStyleByType = (type) => {
    switch (type?.toLowerCase()) {
      case 'priority':
        return {
          bgcolor: '#F59E0B', // Deeper amber color
          color: '#FFFFFF', // White text for contrast
          fontWeight: 'bold',
          borderColor: '#D97706', // Darker border
        };
      case 'count':
        return {
          bgcolor: '#3B82F6', // Deeper blue color
          color: '#FFFFFF', // White text for contrast
          fontWeight: 'bold',
          borderColor: '#2563EB', // Darker border
        };
      default:
        return {
          bgcolor: '#6B7280', // Dark gray for unknown types
          color: '#FFFFFF',
          fontWeight: 'bold',
          borderColor: '#4B5563',
        };
    }
  };

  // Ensure content exists
  const content = tag.content || {};

  // Use content fields only
  const displayName = content.displayName || {};
  const description = content.description || '';
  const defaultFee = content.defaultFee || 0;
  const defaultWage = content.defaultWage || 0;
  const specialRules = content.specialRules || [];
  const isActive = tag.isActive !== undefined ? tag.isActive : true;
  const tagName = content.name || '';

  // Start editing mode
  const handleEdit = () => {
    setIsEditing(true);
    // Create a deep copy of the tag for editing, focusing only on content
    setEditedTag({
      ...tag,
      isActive: isActive,
      content: {
        ...content,
        name: tagName,
        description: description,
        defaultFee: defaultFee,
        defaultWage: defaultWage,
        displayName: { ...displayName },
        specialRules: specialRules.map((rule) => ({
          ...rule,
          actions: { ...rule.actions },
        })),
      },
    });
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    setEditedTag(null);
    setNewLanguage('');
  };

  // Save changes
  const handleSave = () => {
    onSave(editedTag);
    setIsEditing(false);
    setEditedTag(null);
    setNewLanguage('');
  };

  // Handle input changes
  const handleChange = (field, value) => {
    if (field.includes('.')) {
      // Handle nested fields like content.name
      const [parent, child] = field.split('.');
      setEditedTag({
        ...editedTag,
        [parent]: {
          ...editedTag[parent],
          [child]: value,
        },
      });
    } else {
      // Handle top-level fields
      setEditedTag({
        ...editedTag,
        [field]: value,
      });
    }
  };

  // Handle numeric changes (consistent with CreateTagModal)
  const handleNumericChange = (field, value) => {
    // If the value is empty or just a minus sign, allow it (for negative numbers)
    if (value === '' || value === '-') {
      handleChange(field, value);
    } else {
      handleChange(field, Number(value));
    }
  };

  // Handle display name changes
  const handleDisplayNameChange = (langCode, value) => {
    setEditedTag({
      ...editedTag,
      content: {
        ...editedTag.content,
        displayName: {
          ...editedTag.content.displayName,
          [langCode]: value,
        },
      },
    });
  };

  // Remove a display name language
  const handleRemoveDisplayName = (langCode) => {
    const updatedDisplayNames = { ...editedTag.content.displayName };
    delete updatedDisplayNames[langCode];

    setEditedTag({
      ...editedTag,
      content: {
        ...editedTag.content,
        displayName: updatedDisplayNames,
      },
    });
  };

  // Add a new language display name
  const handleAddLanguage = () => {
    if (newLanguage && !editedTag.content.displayName[newLanguage]) {
      setEditedTag({
        ...editedTag,
        content: {
          ...editedTag.content,
          displayName: {
            ...editedTag.content.displayName,
            [newLanguage]: '',
          },
        },
      });
      setNewLanguage('');
    }
  };

  // Handle special rule changes
  const handleRuleChange = (index, field, value) => {
    const updatedRules = [...editedTag.content.specialRules];

    if (field.includes('.')) {
      // Handle nested fields like actions.extraFee
      const [parent, child] = field.split('.');
      updatedRules[index] = {
        ...updatedRules[index],
        [parent]: {
          ...updatedRules[index][parent],
          [child]: value,
        },
      };
    } else {
      // Handle direct fields
      updatedRules[index] = {
        ...updatedRules[index],
        [field]: value,
      };
    }

    setEditedTag({
      ...editedTag,
      content: {
        ...editedTag.content,
        specialRules: updatedRules,
      },
    });
  };

  // Add a new special rule
  const handleAddSpecialRule = () => {
    setEditedTag({
      ...editedTag,
      content: {
        ...editedTag.content,
        specialRules: [
          ...editedTag.content.specialRules,
          {
            type: 'priority', // Default type, consistent with CreateTagModal
            condition: '',
            remark: '',
            actions: { extraFee: 0, extraWage: 0 },
          },
        ],
      },
    });
  };

  // Remove a special rule
  const handleRemoveSpecialRule = (index) => {
    const updatedRules = [...editedTag.content.specialRules];
    updatedRules.splice(index, 1);

    setEditedTag({
      ...editedTag,
      content: {
        ...editedTag.content,
        specialRules: updatedRules,
      },
    });
  };

  // Toggle active status
  const handleToggleActive = () => {
    setEditedTag({
      ...editedTag,
      isActive: !editedTag.isActive,
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        height: '100%',
        maxHeight: '800px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        },
      }}>
      {/* First Line: ID, Name, Active Status */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          pb: 1,
          borderBottom: '1px solid #eee',
        }}>
        <Box display="flex" alignItems="center" flexGrow={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Tag ID: <strong>{tag.id}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Identifier Name:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {tagName}
          </Typography>
        </Box>
        {isEditing ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              color={editedTag.isActive ? 'text.secondary' : 'text.primary'}>
              Inactive
            </Typography>
            <Switch
              checked={editedTag.isActive}
              onChange={handleToggleActive}
              color="success"
            />
            <Typography
              variant="body2"
              color={editedTag.isActive ? 'text.primary' : 'text.secondary'}>
              Active
            </Typography>
          </Stack>
        ) : (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="medium"
          />
        )}
      </Box>

      {/* Content Area with Scrolling */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pt: 1,
          pr: 1, // Add padding to prevent content from hitting scrollbar
          mr: -1, // Offset the padding so the container doesn't expand
        }}>
        {/* Second Line: Default Fee, Default Wage, Description */}
        <Box sx={{ mb: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              {isEditing ? (
                <TextField
                  label="Default Fee"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">￥</InputAdornment>
                      ),
                    },
                    htmlInput: {
                      min: 0, // Ensure `min` is passed to the underlying HTML <input>
                    },
                  }}
                  value={editedTag.content.defaultFee}
                  onChange={(e) =>
                    handleNumericChange('content.defaultFee', e.target.value)
                  }
                  onClick={(e) => {
                    if (e.target instanceof HTMLInputElement) {
                      e.target.select();
                    }
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  <strong>Default Fee:</strong> ¥{defaultFee}
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              {isEditing ? (
                <TextField
                  label="Default Wage"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">￥</InputAdornment>
                      ),
                    },
                    htmlInput: {
                      min: 0, // Ensure `min` is passed to the underlying HTML <input>
                    },
                  }}
                  value={editedTag.content.defaultWage}
                  onChange={(e) =>
                    handleNumericChange('content.defaultWage', e.target.value)
                  }
                  onClick={(e) => {
                    if (e.target instanceof HTMLInputElement) {
                      e.target.select();
                    }
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  <strong>Default Wage:</strong> ¥{defaultWage}
                </Typography>
              )}
            </Grid>
          </Grid>

          {isEditing ? (
            <TextField
              label="Description"
              multiline
              rows={2}
              fullWidth
              sx={{ mt: 1.5 }}
              value={editedTag.content.description || ''}
              onChange={(e) =>
                handleChange('content.description', e.target.value)
              }
            />
          ) : (
            description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Description:</strong> {description}
              </Typography>
            )
          )}
        </Box>

        {/* Third Line: Display Names */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Display Names:</strong>
            </Typography>
            {isEditing && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 120,
                    mr: 1,
                    '& .MuiInputBase-root': {
                      height: 32,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      fontSize: '0.85rem',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      transform: 'translate(14px, 8px) scale(1)',
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)',
                    },
                  }}>
                  <InputLabel id="language-select-label">Language</InputLabel>
                  <Select
                    labelId="language-select-label"
                    value={newLanguage}
                    label="Language"
                    onChange={(e) => setNewLanguage(e.target.value)}>
                    {AVAILABLE_LANGUAGES.map((lang) => {
                      const isUsed = Object.prototype.hasOwnProperty.call(
                        editedTag.content.displayName,
                        lang.code
                      );
                      return (
                        <MenuItem
                          key={lang.code}
                          value={lang.code}
                          disabled={isUsed}
                          sx={isUsed ? { opacity: 0.5 } : {}}>
                          {lang.name} {isUsed && '(in use)'}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={handleAddLanguage}
                  disabled={!newLanguage}
                  sx={{
                    color: 'text.primary',
                    height: 32,
                  }}>
                  Add
                </Button>
              </Box>
            )}
          </Box>
          <Grid container spacing={1}>
            {Object.entries(
              isEditing ? editedTag.content.displayName : displayName
            ).map(([langCode, name]) => (
              <Grid item xs={6} key={langCode}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LanguageIcon
                      fontSize="small"
                      sx={{ mr: 1, color: 'primary.main', fontSize: '1rem' }}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label={
                        AVAILABLE_LANGUAGES.find((l) => l.code === langCode)
                          ?.name || langCode
                      }
                      value={name}
                      onChange={(e) =>
                        handleDisplayNameChange(langCode, e.target.value)
                      }
                    />
                    {Object.keys(editedTag.content.displayName).length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveDisplayName(langCode)}
                        sx={{ ml: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 0.75,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}>
                    <LanguageIcon
                      fontSize="small"
                      sx={{ mr: 1, color: 'primary.main', fontSize: '1rem' }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block">
                        {AVAILABLE_LANGUAGES.find((l) => l.code === langCode)
                          ?.name || langCode}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {name}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
          {isEditing &&
            Object.keys(editedTag.content.displayName).length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, fontStyle: 'italic' }}>
                No display names added yet. Use the dropdown above to add
                languages.
              </Typography>
            )}
        </Box>

        {/* Fourth Line: Special Rules List */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Special Rules:</strong> (
              {isEditing
                ? editedTag.content.specialRules.length
                : specialRules.length}
              )
            </Typography>
            {isEditing && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAddSpecialRule}
                sx={{ color: 'text.primary' }}>
                Add Rule
              </Button>
            )}
          </Box>

          <Box>
            {(isEditing ? editedTag.content.specialRules : specialRules).map(
              (rule, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: isEditing ? 1.5 : 1.2,
                    mb: 1,
                    bgcolor: '#f9f9f9',
                  }}>
                  <Grid container spacing={isEditing ? 2 : 1}>
                    {/* Left column for rule number and type */}
                    <Grid item xs={3}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                        }}>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{
                            alignSelf: 'flex-start',
                            mb: 1,
                            fontWeight: 700,
                            fontSize: '0.85rem',
                          }}>
                          Rule #{index + 1}
                        </Typography>
                        <Chip
                          label={rule.type || 'Unknown'}
                          size="small"
                          variant="outlined"
                          sx={{
                            alignSelf: 'flex-start',
                            height: 20,
                            '& .MuiChip-label': getChipStyleByType(rule.type),
                          }}
                        />
                        {isEditing && (
                          <Box sx={{ mt: 'auto', pt: 1 }}>
                            {isEditing && (
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{ mt: 1, mb: 1 }}>
                                <InputLabel>Rule Type</InputLabel>
                                <Select
                                  value={rule.type || 'priority'}
                                  label="Rule Type"
                                  onChange={(e) =>
                                    handleRuleChange(
                                      index,
                                      'type',
                                      e.target.value
                                    )
                                  }>
                                  <MenuItem value="priority">Priority</MenuItem>
                                  <MenuItem value="count">Count</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveSpecialRule(index)}
                              sx={{
                                alignSelf: 'flex-start',
                                p: 0.5,
                              }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Right column for rule content */}
                    <Grid item xs={9}>
                      {/* Rule first line: Condition */}
                      <Box sx={{ mb: isEditing ? 1 : 0.3 }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            fullWidth
                            label="Condition"
                            placeholder={
                              rule.type === 'count'
                                ? 'e.g. count > 0 && count < 100'
                                : 'e.g. priority === 3'
                            }
                            value={rule.condition || ''}
                            onChange={(e) =>
                              handleRuleChange(
                                index,
                                'condition',
                                e.target.value
                              )
                            }
                          />
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 400 }}>
                            <strong>Condition:</strong>{' '}
                            {rule.condition || 'None'}
                          </Typography>
                        )}
                      </Box>

                      {/* Rule second line: Extra Fee and Extra Wage */}
                      <Box sx={{ mb: isEditing ? 1 : 0.3 }}>
                        {isEditing ? (
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Extra Fee"
                                type="number"
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ￥
                                      </InputAdornment>
                                    ),
                                  },
                                  htmlInput: {
                                    min: 0, // Ensure `min` is passed to the underlying HTML <input>
                                  },
                                }}
                                value={rule.actions?.extraFee ?? 0}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'actions.extraFee',
                                    e.target.value
                                  )
                                }
                                onClick={(e) => {
                                  if (e.target instanceof HTMLInputElement) {
                                    e.target.select();
                                  }
                                }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Extra Wage"
                                type="number"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ¥
                                    </InputAdornment>
                                  ),
                                }}
                                value={rule.actions?.extraWage ?? 0}
                                onChange={(e) =>
                                  handleRuleChange(
                                    index,
                                    'actions.extraWage',
                                    e.target.value
                                  )
                                }
                                onClick={(e) => {
                                  if (e.target instanceof HTMLInputElement) {
                                    e.target.select();
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        ) : (
                          <Box sx={{ display: 'flex', mt: -0.2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mr: 3 }}>
                              <strong>Extra Fee:</strong> ¥
                              {(rule.actions && rule.actions.extraFee) || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Extra Wage:</strong> ¥
                              {(rule.actions && rule.actions.extraWage) || 0}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Rule third line: Remark only */}
                      <Box sx={{ mt: isEditing ? 0 : -0.2 }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            fullWidth
                            label="Remark"
                            placeholder="Short description of this rule"
                            value={rule.remark || ''}
                            onChange={(e) =>
                              handleRuleChange(index, 'remark', e.target.value)
                            }
                          />
                        ) : (
                          rule.remark && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Remark:</strong> {rule.remark}
                            </Typography>
                          )
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )
            )}
            {isEditing && editedTag.content.specialRules.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}>
                No special rules added yet. Click &quot;Add Rule&quot; to create
                one.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Actions Section with Dates */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          pt: 2,
          borderTop: '1px solid #eee',
        }}>
        {/* Dates stacked at beginning of line */}
        <Box>
          {tag.updatedAt && (
            <Typography
              variant="caption"
              display="block"
              color="text.secondary">
              Updated: {format(new Date(tag.updatedAt), 'yyyy-MM-dd HH:mm')}
            </Typography>
          )}
          {tag.createdAt && (
            <Typography
              variant="caption"
              display="block"
              color="text.secondary">
              Created: {format(new Date(tag.createdAt), 'yyyy-MM-dd HH:mm')}
            </Typography>
          )}
        </Box>

        {/* Action buttons at end */}
        <Box sx={{ ml: 'auto' }}>
          {isEditing ? (
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCancel}
                sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ color: '#ffffff' }}>
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              sx={{ color: '#ffffff' }}
              onClick={handleEdit}>
              Edit
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

TagCard.propTypes = {
  tag: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    organizationId: PropTypes.number.isRequired,
    description: PropTypes.string,
    metadata: PropTypes.object,
    isActive: PropTypes.bool,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    content: PropTypes.shape({
      name: PropTypes.string,
      displayName: PropTypes.object,
      description: PropTypes.string,
      defaultFee: PropTypes.number,
      defaultWage: PropTypes.number,
      specialRules: PropTypes.array,
    }),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default TagCard;
